import { formatCompact } from '../charts/chart-utils';
import { automatedManualOf } from '../charts/trend-split';
import {
  AutomationDashboardSummaryMock,
  AutomationResultsMock,
  KPIDataMock,
  TrendDataPointMock,
} from '../charts/chart-types';
import {
  InsightsFindingRow,
  InsightsKpiDirection,
  InsightsKpiRow,
  InsightsPanelProps,
} from './insights-panel';

/**
 * Pre-formatted percent for rates (e.g. `78.84` → `'78.8%'`).
 * Mirrors `formatPct` in `cmx-argus/AutomationKPITile.jsx`.
 */
const formatPct = (n: number | undefined): string =>
  `${(n ?? 0).toFixed(1)}%`;

const safeNum = (n: number | undefined): number => n ?? 0;

const calcSum = <T extends Record<string, number>>(arr: T[], key: keyof T): number =>
  arr.reduce((sum, d) => sum + safeNum(d[key] as number | undefined), 0);

const calcAvg = <T extends Record<string, number>>(arr: T[], key: keyof T): number =>
  arr.length > 0 ? calcSum(arr, key) / arr.length : 0;

interface DerivedTrendItem extends TrendDataPointMock {
  automated: number;
  manual: number;
  total: number;
}

const splitWeekdayWeekend = (
  trend: TrendDataPointMock[],
): { weekdays: DerivedTrendItem[]; weekends: DerivedTrendItem[]; allDays: DerivedTrendItem[] } => {
  const weekdays: DerivedTrendItem[] = [];
  const weekends: DerivedTrendItem[] = [];
  const allDays: DerivedTrendItem[] = [];

  trend.forEach((d) => {
    const { automated, manual } = automatedManualOf(d);
    const total = automated + manual;
    const item: DerivedTrendItem = { ...d, automated, manual, total };

    const dayOfWeek = new Date(`${d.date}T00:00:00`).getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      weekends.push(item);
    } else {
      weekdays.push(item);
    }
    allDays.push(item);
  });

  return { weekdays, weekends, allDays };
};

/**
 * Computed metric block — ported verbatim from the cmx-argus reference impl
 * (`AutomationKPITile.jsx::calculateAllMetrics`). Exposed so consumers and
 * tests can verify field parity without re-rendering the panel.
 */
export interface InsightsMetrics {
  daysCount: number;
  weekdayCount: number;
  weekendCount: number;
  totalCases: number;
  prevTotalCases: number;
  automatedCases: number;
  prevAutomatedCases: number;
  manualCases: number;
  prevManualCases: number;
  eligibleCases: number;
  prevEligibleCases: number;
  avgDailyVolume: number;
  prevAvgDailyVolume: number;
  avgDailyAutomated: number;
  avgDailyManual: number;
  automationRate: number;
  prevAutomationRate: number;
  eligibilityRate: number;
  prevEligibilityRate: number;
  weekdayAvgVolume: number;
  weekendAvgVolume: number;
  weekdayAvgRate: number;
  weekendAvgRate: number;
  highConfCases: number;
  ruleBased: number;
  highConfPct: number;
}

export const calculateInsightsMetrics = (
  current: KPIDataMock | undefined,
  previous: KPIDataMock | undefined,
  trend: TrendDataPointMock[] = [],
  automationResults?: AutomationResultsMock,
): InsightsMetrics => {
  const { weekdays, weekends, allDays } = splitWeekdayWeekend(trend);
  const daysCount = allDays.length;
  const prevDaysCount = daysCount;

  const totalCases = safeNum(current?.total_cases);
  const automatedCases = safeNum(current?.automated_cases);
  const manualCases = safeNum(current?.manual_cases);
  const eligibleCases = safeNum(current?.eligible_cases);
  const automationRate = safeNum(current?.automation_rate);

  const prevTotalCases = safeNum(previous?.total_cases);
  const prevAutomatedCases = safeNum(previous?.automated_cases);
  const prevManualCases = safeNum(previous?.manual_cases);
  const prevEligibleCases = safeNum(previous?.eligible_cases);
  const prevAutomationRate = safeNum(previous?.automation_rate);

  const eligibilityRate = totalCases > 0 ? (eligibleCases / totalCases) * 100 : 0;
  const prevEligibilityRate =
    prevTotalCases > 0 ? (prevEligibleCases / prevTotalCases) * 100 : 0;

  const avgDailyVolume = daysCount > 0 ? totalCases / daysCount : 0;
  const prevAvgDailyVolume =
    prevDaysCount > 0 ? prevTotalCases / prevDaysCount : 0;
  const avgDailyAutomated = daysCount > 0 ? automatedCases / daysCount : 0;
  const avgDailyManual = daysCount > 0 ? manualCases / daysCount : 0;

  const weekdayAvgVolume = calcAvg(weekdays, 'total');
  const weekendAvgVolume = calcAvg(weekends, 'total');
  const weekdayAvgRate = calcAvg(weekdays, 'automation_rate');
  const weekendAvgRate = calcAvg(weekends, 'automation_rate');

  // The period's results breakdown is the authoritative high-confidence total. The per-day
  // four-way trend fields it used to be summed from are absent in live responses.
  const highConfCases =
    automationResults?.automated_high_confidence ??
    trend.reduce((sum, d) => sum + safeNum(d.automated_high_confidence), 0);
  const ruleBased = automatedCases - highConfCases;
  const highConfPct =
    automatedCases > 0 ? (highConfCases / automatedCases) * 100 : 0;

  return {
    daysCount,
    weekdayCount: weekdays.length,
    weekendCount: weekends.length,
    totalCases,
    prevTotalCases,
    automatedCases,
    prevAutomatedCases,
    manualCases,
    prevManualCases,
    eligibleCases,
    prevEligibleCases,
    avgDailyVolume,
    prevAvgDailyVolume,
    avgDailyAutomated,
    avgDailyManual,
    automationRate,
    prevAutomationRate,
    eligibilityRate,
    prevEligibilityRate,
    weekdayAvgVolume,
    weekendAvgVolume,
    weekdayAvgRate,
    weekendAvgRate,
    highConfCases,
    ruleBased,
    highConfPct,
  };
};

interface DeltaInput {
  current: number;
  previous: number;
  isRate?: boolean;
  /** Inverse means up=bad (e.g. Manual cases). */
  inverse?: boolean;
}

interface DeltaResult {
  display: string;
  direction: InsightsKpiDirection;
  positive: boolean;
}

const buildDelta = ({
  current,
  previous,
  isRate = false,
  inverse = false,
}: DeltaInput): DeltaResult => {
  if (!previous) {
    return { display: '\u2014', direction: 'flat', positive: false };
  }
  const raw = isRate ? current - previous : ((current - previous) / previous) * 100;

  if (Math.abs(raw) < 0.5) {
    return { display: '~0', direction: 'flat', positive: false };
  }

  const magnitude = isRate
    ? `${Math.abs(raw).toFixed(1)}pp`
    : `${Math.abs(raw).toFixed(0)}%`;
  const direction: InsightsKpiDirection = raw > 0 ? 'up' : 'down';
  const positive = inverse ? raw < 0 : raw > 0;
  return { display: magnitude, direction, positive };
};

const buildKpiRow = (
  label: string,
  current: number,
  previous: number,
  opts: { isRate?: boolean; inverse?: boolean; highlight?: boolean } = {},
): InsightsKpiRow => {
  const { display, direction, positive } = buildDelta({
    current,
    previous,
    isRate: opts.isRate,
    inverse: opts.inverse,
  });
  return {
    label,
    value: opts.isRate ? formatPct(current) : formatCompact(current),
    delta: display,
    direction,
    positive,
    highlight: opts.highlight,
  };
};

const buildKpiRows = (m: InsightsMetrics): InsightsKpiRow[] => [
  buildKpiRow('Total Volume', m.totalCases, m.prevTotalCases),
  buildKpiRow('Automated', m.automatedCases, m.prevAutomatedCases),
  buildKpiRow('Manual', m.manualCases, m.prevManualCases, { inverse: true }),
  buildKpiRow('Automation Rate', m.automationRate, m.prevAutomationRate, {
    isRate: true,
    highlight: true,
  }),
  buildKpiRow('Eligibility Rate', m.eligibilityRate, m.prevEligibilityRate, {
    isRate: true,
  }),
  buildKpiRow('Avg Daily Volume', m.avgDailyVolume, m.prevAvgDailyVolume),
];

const buildFindingRows = (m: InsightsMetrics): InsightsFindingRow[] => {
  const rows: InsightsFindingRow[] = [];

  if (m.weekdayCount > 0 && m.weekendCount > 0) {
    rows.push({
      label: 'Weekday vs Weekend',
      primary: `${formatCompact(m.weekdayAvgVolume)}/d @ ${formatPct(m.weekdayAvgRate)} vs ${formatCompact(
        m.weekendAvgVolume,
      )}/d @ ${formatPct(m.weekendAvgRate)}`,
    });
  }

  rows.push({
    label: 'High Conf vs Rule',
    primary: `${formatCompact(m.highConfCases)} (${formatPct(m.highConfPct)}) : ${formatCompact(
      m.ruleBased,
    )} (${formatPct(100 - m.highConfPct)})`,
  });

  rows.push({
    label: 'Daily Split',
    primary: `${formatCompact(m.avgDailyAutomated)} auto + ${formatCompact(m.avgDailyManual)} manual / day`,
  });

  rows.push({
    label: 'Eligible Cases',
    primary: `${formatCompact(m.eligibleCases)} of ${formatCompact(m.totalCases)} (${formatPct(
      m.eligibilityRate,
    )})`,
  });

  return rows;
};

export interface ToInsightsPanelOptions {
  /** Override the default `'KPIs'` sub-header period suffix. */
  kpiPeriodLabel?: string;
}

/**
 * Map an `/api/automation-dashboard/summary` response into `<InsightsPanel>`
 * props. Keeps CDMX-14949 page wire-up to a one-liner per panel.
 */
export const toInsightsPanelProps = (
  response: AutomationDashboardSummaryMock,
  options: ToInsightsPanelOptions = {},
): InsightsPanelProps => {
  const { current, previous } = response.data.kpis;
  const metrics = calculateInsightsMetrics(
    current,
    previous,
    response.data.trend,
    response.data.automation_results,
  );
  const periodLabel =
    options.kpiPeriodLabel ?? `(${metrics.daysCount} days, delta vs prior period)`;

  return {
    kpiPeriodLabel: periodLabel,
    kpis: buildKpiRows(metrics),
    findings: buildFindingRows(metrics),
  };
};
